import React, { useEffect } from 'react';
import './HelpGuide.css';

// Helper function to get image path
const getImagePath = (imageName) => {
  // Try multiple path variations
  const basePath = process.env.PUBLIC_URL || '';
  const paths = [
    `${basePath}/HelpImages/${imageName}`,
    `/HelpImages/${imageName}`,
    `${window.location.origin}/HelpImages/${imageName}`,
  ];
  return paths[0]; // Use first path, fallback handled by onError
};

const HelpGuide = ({ isOpen, onClose }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      // Add class to body to prevent scroll
      document.body.classList.add('modal-open');
      // Disable body scroll and save scroll position
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Re-enable body scroll when modal closes
        document.body.classList.remove('modal-open');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="help-guide-overlay" onClick={onClose}>
      <div className="help-guide-modal" onClick={(e) => e.stopPropagation()}>
        <div className="help-guide-header">
          <h2>📚 SLN Book World - User Workflow Guide</h2>
          <button className="help-guide-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="help-guide-content">
          <div className="help-section">
            <h3>Welcome to SLN Book World!</h3>
            <p className="tagline">"All your child's school books, in one click!"</p>
            <p>This guide will help you understand how to use the website to purchase school book kits for your child.</p>
          </div>

          <div className="help-section">
            <h3>📋 Quick Overview</h3>
            <p>SLN Book World is an online platform where parents can:</p>
            <ul>
              <li>Browse schools and find their child's school</li>
              <li>View available book kits for different standards (1-10)</li>
              <li>Purchase complete book kits with secure payment</li>
              <li>Track orders and receive delivery updates</li>
            </ul>
          </div>

          <div className="help-section">
            <h3>🚀 Getting Started</h3>
            
            <div className="help-step">
              <h4>Step 1: Register Your Account</h4>
              <ol>
                <li><strong>Visit the website</strong>: Go to www.slnbooks.com</li>
                <li><strong>Click "Register"</strong> in the top navigation menu</li>
                <li><strong>Fill in your details</strong>:
                  <ul>
                    <li>Full Name</li>
                    <li>Email Address</li>
                    <li>Password (choose a strong password)</li>
                    <li>Phone Number</li>
                    <li>Any other required information</li>
                  </ul>
                </li>
                <li><strong>Click "Register"</strong> to create your account</li>
              </ol>
              <img 
                src={getImagePath('RegisterScreen.png')}
                alt="Registration Page" 
                className="help-screenshot"
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  console.error('Trying fallback path...');
                  e.target.src = `/HelpImages/RegisterScreen.png`;
                }}
              />
              <p className="help-note"><strong>Note:</strong> You must be logged in to add items to cart and make purchases.</p>
            </div>

            <div className="help-step">
              <h4>Step 2: Browse Schools</h4>
              <ol>
                <li><strong>Click "Schools"</strong> in the navigation menu</li>
                <li><strong>Search for your child's school</strong> using:
                  <ul>
                    <li><strong>City Filter</strong>: Select your city from the dropdown</li>
                    <li><strong>Board Type Filter</strong>: Choose CBSE, ICSE, State Board, etc.</li>
                    <li><strong>School Name Search</strong>: Type the school name in the search box</li>
                  </ul>
                </li>
                <li><strong>Click on a school</strong> from the results to view its details</li>
              </ol>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/HelpImages/SchoolsList_Screen.png`}
                alt="Schools Directory Page" 
                className="help-screenshot"
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>

            <div className="help-step">
              <h4>Step 3: View Available Book Kits</h4>
              <p>Once you've selected a school:</p>
              <ol>
                <li><strong>View school information</strong>: You'll see school details including name, address, and board type</li>
                <li><strong>Browse book kits</strong>: Scroll down to see available kits for different standards (1-10)</li>
                <li><strong>View kit details</strong>: Each kit shows:
                  <ul>
                    <li>Standard/Grade level</li>
                    <li>Price</li>
                    <li>Complete list of books included (subjects and publishers)</li>
                    <li>Kit description</li>
                  </ul>
                </li>
              </ol>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/HelpImages/Standards_Kits.png`}
                alt="School Page with Book Kits" 
                className="help-screenshot"
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>

            <div className="help-step">
              <h4>Step 4: Add Kits to Cart</h4>
              <ol>
                <li><strong>Select the standard</strong> you need (e.g., Standard 5)</li>
                <li><strong>Review the kit contents</strong> to ensure it matches your requirements</li>
                <li><strong>Click "Add to Cart"</strong> button</li>
                <li><strong>Confirm</strong>: You'll see a success message and the cart icon will update</li>
              </ol>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/HelpImages/Standards_Kits.png`}
                alt="Add to Cart - Book Kits Selection" 
                className="help-screenshot"
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
              <div className="help-tips">
                <strong>Tips:</strong>
                <ul>
                  <li>You can add multiple kits to your cart (e.g., one for each child)</li>
                  <li>You can continue browsing and add more kits before checkout</li>
                  <li>The cart icon in the header shows the number of items</li>
                </ul>
              </div>
            </div>

            <div className="help-step">
              <h4>Step 5: Review Your Cart</h4>
              <ol>
                <li><strong>Click the cart icon</strong> in the header (or go to "Cart" from the menu)</li>
                <li><strong>Review your items</strong>:
                  <ul>
                    <li>Kit name and standard</li>
                    <li>Price per kit</li>
                    <li>Quantity (you can increase/decrease or remove items)</li>
                    <li>Subtotal</li>
                  </ul>
                </li>
                <li><strong>Make any changes</strong>:
                  <ul>
                    <li>Update quantities using +/- buttons</li>
                    <li>Remove items you don't need</li>
                  </ul>
                </li>
                <li><strong>Click "Proceed to Checkout"</strong> when ready</li>
              </ol>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/HelpImages/CheckOutScreen.png`}
                alt="Shopping Cart and Checkout Page" 
                className="help-screenshot"
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>

            <div className="help-step">
              <h4>Step 6: Checkout Process</h4>
              
              <h5>6.1 Review Order Summary</h5>
              <ul>
                <li>Verify all kits in your order</li>
                <li>Check quantities and prices</li>
                <li>Review subtotal</li>
              </ul>

              <h5>6.2 Choose Delivery Option</h5>
              <p><strong>Two delivery options available:</strong></p>
              <div className="delivery-options">
                <div className="delivery-option">
                  <h6>1. School Delivery (Pickup) - FREE</h6>
                  <ul>
                    <li>Books will be delivered to your child's school</li>
                    <li>You can pick them up from school</li>
                    <li>No additional charges</li>
                  </ul>
                </div>
                <div className="delivery-option">
                  <h6>2. Home Delivery - ₹150 per kit</h6>
                  <ul>
                    <li>Books will be delivered to your home address</li>
                    <li>Enter your complete delivery address</li>
                    <li>Additional ₹150 charged per kit</li>
                  </ul>
                </div>
              </div>
              <p><strong>Select your preferred option</strong> using the radio buttons.</p>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/HelpImages/CheckOutScreen.png`}
                alt="Checkout Page with Delivery Options" 
                className="help-screenshot"
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />

              <h5>6.3 Review Final Total</h5>
              <ul>
                <li>Subtotal (sum of all kits)</li>
                <li>Delivery charges (if home delivery selected)</li>
                <li><strong>Grand Total</strong> (final amount to pay)</li>
              </ul>
            </div>

            <div className="help-step">
              <h4>Step 7: Make Payment</h4>
              <ol>
                <li><strong>Click "Proceed to Payment"</strong> button</li>
                <li><strong>You'll be redirected</strong> to the secure HDFC payment gateway</li>
                <li><strong>Enter payment details</strong>:
                  <ul>
                    <li>Card number (or use other payment methods available)</li>
                    <li>Expiry date</li>
                    <li>CVV</li>
                    <li>Cardholder name</li>
                  </ul>
                </li>
                <li><strong>Complete the payment</strong> following the gateway instructions</li>
                <li><strong>Wait for confirmation</strong> - you'll be redirected back to the website</li>
              </ol>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/HelpImages/Payment_Screen.png`}
                alt="Payment Gateway" 
                className="help-screenshot"
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
              <p className="help-note"><strong>Security:</strong> All payments are processed through HDFC Smart Gateway with bank-level security.</p>
            </div>

            <div className="help-step">
              <h4>Step 8: Order Confirmation</h4>
              <p>After successful payment:</p>
              <ol>
                <li><strong>Order Confirmation Page</strong> will appear showing:
                  <ul>
                    <li>Order number</li>
                    <li>Order date</li>
                    <li>Items ordered</li>
                    <li>Delivery address/option</li>
                    <li>Total amount paid</li>
                    <li>Order status</li>
                  </ul>
                </li>
                <li><strong>Email Confirmation</strong>: You'll receive an email confirmation at your registered email address</li>
                <li><strong>Save your order number</strong> for future reference</li>
              </ol>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/HelpImages/OrdersScreen.png`}
                alt="Order Confirmation and Orders Page" 
                className="help-screenshot"
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>

            <div className="help-step">
              <h4>Step 9: Track Your Orders</h4>
              <ol>
                <li><strong>Click "Orders"</strong> in the navigation menu (when logged in)</li>
                <li><strong>View all your orders</strong>:
                  <ul>
                    <li>Order number</li>
                    <li>Order date</li>
                    <li>Items ordered</li>
                    <li>Order status (Pending, Processing, Shipped, Delivered)</li>
                    <li>Total amount</li>
                  </ul>
                </li>
                <li><strong>Click on an order</strong> to view detailed information</li>
              </ol>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/HelpImages/OrdersScreen.png`}
                alt="Orders Page" 
                className="help-screenshot"
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>

          <div className="help-section">
            <h3>🔄 Common Workflows</h3>
            
            <div className="help-workflow">
              <h4>Purchasing Books for Multiple Children</h4>
              <ol>
                <li>Add kit for first child → Add to Cart</li>
                <li>Browse again → Select second child's school</li>
                <li>Add kit for second child → Add to Cart</li>
                <li>Continue until all kits are added</li>
                <li>Go to Cart → Review all items</li>
                <li>Proceed to Checkout → Complete payment</li>
              </ol>
            </div>

            <div className="help-workflow">
              <h4>Changing Your Order</h4>
              <p><strong>Before Payment:</strong></p>
              <ul>
                <li>Go to Cart → Modify quantities or remove items</li>
                <li>Proceed to Checkout</li>
              </ul>
              <p><strong>After Payment:</strong></p>
              <ul>
                <li>Contact customer support via the Contact page</li>
                <li>Provide your order number</li>
              </ul>
            </div>
          </div>

          <div className="help-section">
            <h3>📞 Need Help?</h3>
            
            <div className="help-step">
              <h4>Contact Support</h4>
              <ol>
                <li><strong>Click "Contact"</strong> in the navigation menu</li>
                <li><strong>Fill out the contact form</strong>:
                  <ul>
                    <li>Your name</li>
                    <li>Email address</li>
                    <li>Subject</li>
                    <li>Message</li>
                  </ul>
                </li>
                <li><strong>Submit</strong> - Our team will respond to your query</li>
              </ol>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/HelpImages/Contact_Us_Screen.png`}
                alt="Contact Support Page" 
                className="help-screenshot"
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>

            <div className="help-step">
              <h4>Other Resources</h4>
              <ul>
                <li><strong>Privacy Policy</strong>: View our privacy policy</li>
                <li><strong>Terms & Conditions</strong>: Read terms of service</li>
                <li><strong>Shipping & Delivery</strong>: Information about delivery timelines</li>
                <li><strong>Refund & Cancellation</strong>: Policy details</li>
              </ul>
            </div>
          </div>

          <div className="help-section">
            <h3>🔐 Account Management</h3>
            
            <div className="help-step">
              <h4>Change Password</h4>
              <ol>
                <li>Log in to your account</li>
                <li>Go to your profile/account settings</li>
                <li>Select "Change Password"</li>
                <li>Enter current password and new password</li>
                <li>Save changes</li>
              </ol>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/HelpImages/ChangePassword_LogOut.png`}
                alt="Change Password and Logout" 
                className="help-screenshot"
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>

            <div className="help-step">
              <h4>Logout</h4>
              <p>Click "Logout" in the navigation menu to securely log out of your account.</p>
            </div>
          </div>

          <div className="help-section">
            <h3>⚠️ Important Notes</h3>
            <ol>
              <li><strong>Login Required</strong>: You must be logged in to add items to cart and make purchases</li>
              <li><strong>School Selection</strong>: Make sure you select the correct school for your child</li>
              <li><strong>Standard Selection</strong>: Double-check the standard/grade before adding to cart</li>
              <li><strong>Delivery Address</strong>: For home delivery, ensure your address is complete and accurate</li>
              <li><strong>Payment</strong>: Keep your payment confirmation and order number safe</li>
              <li><strong>Order Tracking</strong>: Check your Orders page regularly for status updates</li>
            </ol>
          </div>

          <div className="help-section">
            <h3>✅ Quick Checklist</h3>
            <p>Before placing an order, ensure:</p>
            <div className="checklist">
              <label><input type="checkbox" disabled /> You are logged in to your account</label>
              <label><input type="checkbox" disabled /> You've selected the correct school</label>
              <label><input type="checkbox" disabled /> You've chosen the right standard/grade</label>
              <label><input type="checkbox" disabled /> You've reviewed the kit contents</label>
              <label><input type="checkbox" disabled /> You've selected delivery option (School/Home)</label>
              <label><input type="checkbox" disabled /> You have payment method ready</label>
              <label><input type="checkbox" disabled /> Your delivery address is correct (if home delivery)</label>
            </div>
          </div>

          <div className="help-section">
            <h3>🎯 Summary</h3>
            <p><strong>The Complete Flow:</strong></p>
            <div className="flow-diagram">
              <p>Register → Login → Browse Schools → Select School → View Kits → Add to Cart → Review Cart → Checkout → Choose Delivery → Make Payment → Order Confirmation → Track Orders</p>
            </div>
            <p><strong>Estimated Time:</strong> 5-10 minutes for complete purchase process</p>
          </div>

          <div className="help-section help-footer">
            <h3>Happy Shopping! 📚</h3>
            <p>For any questions or support, please contact us through the Contact page.</p>
            <p className="help-updated">Last Updated: 2025</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpGuide;
